/* ==========================================================================
   DISSOLV — theme.js
   Cart engine, drawers, product forms, bundle logic, sticky bars, accordions.
   Vanilla JS, no dependencies. Defensive everywhere — every feature no-ops
   cleanly if its markup is absent.
   ========================================================================== */
(() => {
  'use strict';

  const D = window.Dissolv = Object.assign({
    moneyFormat: '${{amount}}',
    currency: 'AUD',
    freeShippingThreshold: 6900,
    motion: { enabled: true, intensity: 'standard' },
    routes: { cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update' },
    strings: {}
  }, window.Dissolv || {});

  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  /* ---------- Money ---------- */
  function formatMoney(cents, format = D.moneyFormat) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const num = parseInt(cents, 10) || 0;

    function delimited(number, precision = 2, thousands = ',', decimal = '.') {
      const negative = number < 0 ? '-' : '';
      const fixed = (Math.abs(number) / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return negative + dollars + (parts[1] ? decimal + parts[1] : '');
    }
    switch ((format.match(placeholderRegex) || [])[1]) {
      case 'amount': value = delimited(num, 2); break;
      case 'amount_no_decimals': value = delimited(num, 0); break;
      case 'amount_with_comma_separator': value = delimited(num, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimited(num, 0, '.'); break;
      default: value = delimited(num, 2);
    }
    return format.replace(placeholderRegex, value);
  }
  D.formatMoney = formatMoney;

  /* ---------- Focus trap ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';
  function trapFocus(container, e) {
    const nodes = qsa(FOCUSABLE, container).filter(el => el.offsetParent !== null);
    if (!nodes.length) return;
    const first = nodes[0], last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }

  /* ---------- Drawers ---------- */
  const overlay = qs('[data-overlay]');
  const Drawers = {
    active: null,
    lastTrigger: null,
    open(name, trigger) {
      const el = qs(`[data-drawer="${name}"]`);
      if (!el) return;
      if (this.active && this.active !== el) this.close(true);
      // Signals third-party overlays (Shopify Inbox bubble) to get out of the
      // way while a drawer is up: its z-index (~2^31) beats anything we set.
      document.documentElement.classList.add('drawer-open');
      this.active = el;
      this.lastTrigger = trigger || document.activeElement;
      el.classList.add('is-open');
      el.removeAttribute('aria-hidden');
      if (overlay) { overlay.hidden = false; requestAnimationFrame(() => overlay.classList.add('is-visible')); }
      document.documentElement.style.overflow = 'hidden';
      const focusEl = qs('[data-autofocus]', el) || qs(FOCUSABLE, el);
      setTimeout(() => focusEl && focusEl.focus({ preventScroll: true }), 120);
      el.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    },
    close(keepOverlay) {
      if (!this.active) return;
      document.documentElement.classList.remove('drawer-open');
      const el = this.active;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      this.active = null;
      if (!keepOverlay && overlay) {
        overlay.classList.remove('is-visible');
        setTimeout(() => { if (!this.active) overlay.hidden = true; }, 420);
      }
      document.documentElement.style.overflow = '';
      if (this.lastTrigger && this.lastTrigger.focus) this.lastTrigger.focus({ preventScroll: true });
    }
  };
  D.Drawers = Drawers;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) { e.preventDefault(); Drawers.open(opener.dataset.drawerOpen, opener); return; }
    if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); Drawers.close(); return; }
    if (e.target === overlay) Drawers.close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && Drawers.active) Drawers.close();
    if (e.key === 'Tab' && Drawers.active) trapFocus(Drawers.active, e);
  });

  /* ---------- Cart ---------- */
  const Cart = {
    state: null,
    listeners: [],
    onChange(fn) { this.listeners.push(fn); },
    emit() { this.listeners.forEach(fn => { try { fn(this.state); } catch (err) { console.error(err); } }); },

    async refresh() {
      try {
        const res = await fetch(`${D.routes.cart}.js`, { headers: { Accept: 'application/json' } });
        this.state = await res.json();
        this.emit();
      } catch (e) { /* offline/preview */ }
      return this.state;
    },

    async request(url, payload) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw Object.assign(new Error(data.description || data.message || 'Cart error'), { data });
      return data;
    },

    // Serialize all cart mutations: Shopify re-keys a line when an automatic
    // discount tier starts/stops applying, so a second request racing the
    // first can carry a stale key and 400. Queueing keeps mutations ordered.
    _chain: Promise.resolve(),
    enqueue(fn) {
      const run = this._chain.then(fn, fn);
      this._chain = run.catch(() => {});
      return run;
    },

    async add(items, openDrawer = true) {
      const payload = { items: Array.isArray(items) ? items : [items], sections: 'cart-drawer' };
      const drawerEl = qs('[data-drawer="cart"]');
      if (openDrawer) {
        // Optimistic: open instantly, veil while the fresh contents arrive
        if (drawerEl) drawerEl.classList.add('is-busy');
        Drawers.open('cart');
      }
      try {
        return await this.enqueue(async () => {
          const data = await this.request(`${D.routes.cartAdd}.js`, payload);
          if (data.sections) renderCartSections(data.sections);
          this.refresh();
          return data;
        });
      } finally {
        if (drawerEl) drawerEl.classList.remove('is-busy');
      }
    },

    change(payload) {
      payload.sections = 'cart-drawer';
      return this.enqueue(async () => {
        // Re-resolve the line key at send time: automatic discounts re-key a
        // line when a tier starts/stops applying, so a key captured before the
        // previous queued mutation may be stale by now.
        if (typeof payload.id === 'string' && payload.id.includes(':') && this.state) {
          const items = this.state.items || [];
          const variantId = payload.id.split(':')[0];
          const live = items.find(i => i.key === payload.id) ||
                       items.find(i => String(i.variant_id) === variantId);
          if (live) payload.id = live.key;
        }
        const data = await this.request(`${D.routes.cartChange}.js`, payload);
        if (data.sections) renderCartSections(data.sections);
        await this.refresh();
        return data;
      });
    },

    update(payload) {
      payload.sections = 'cart-drawer';
      return this.enqueue(async () => {
        const data = await this.request(`${D.routes.cartUpdate}.js`, payload);
        if (data.sections) renderCartSections(data.sections);
        await this.refresh();
        return data;
      });
    }
  };
  D.Cart = Cart;

  function renderCartSections(sections) {
    const html = sections['cart-drawer'];
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cart-drawer-content]');
    const target = qs('[data-cart-drawer-content]');
    if (fresh && target) target.innerHTML = fresh.innerHTML;
  }

  /* Header count + shipping bars react to cart state */
  Cart.onChange((cart) => {
    if (!cart) return;
    qsa('[data-cart-count]').forEach(el => {
      const prev = el.dataset.count;
      el.dataset.count = cart.item_count;
      el.textContent = cart.item_count;
      if (prev !== String(cart.item_count)) {
        el.classList.remove('is-pulsing'); void el.offsetWidth; el.classList.add('is-pulsing');
      }
    });
    qsa('[data-shipping-bar]').forEach(bar => {
      const threshold = parseInt(bar.dataset.threshold, 10) || D.freeShippingThreshold;
      const remaining = threshold - cart.total_price;
      const label = qs('[data-shipping-label]', bar);
      const fill = qs('[data-shipping-fill]', bar);
      if (label) {
        label.innerHTML = remaining > 0
          ? (D.strings.freeShippingProgress || "You're [amount] away from FREE shipping").replace('[amount]', `<strong>${formatMoney(remaining)}</strong>`)
          : (D.strings.freeShippingUnlocked || 'Your order qualifies for FREE shipping');
      }
      if (fill) {
        fill.style.width = `${Math.min(100, (cart.total_price / threshold) * 100)}%`;
        fill.classList.toggle('shipping-bar__fill--complete', remaining <= 0);
      }
      const note = qs('[data-shipping-note]', bar);
      if (note) note.hidden = remaining <= 0;
    });
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
  });

  /* ---------- Buttons: loading state helper ---------- */
  function withLoading(btn, promise) {
    if (!btn) return promise;
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    return promise.finally(() => {
      btn.classList.remove('is-loading');
      btn.removeAttribute('aria-busy');
    });
  }

  function announceError(err) {
    console.warn('[Dissolv cart]', err);
    // Stale line-key race (Shopify re-keys lines when automatic discounts kick
    // in): not a user-facing problem — resync the drawer silently instead of
    // flashing Shopify's raw "no valid id or line parameter" message.
    if (err && /valid id or line/i.test(err.message || '')) { Cart.refresh(); return; }
    const msg = (err && err.message) || D.strings.cartError || 'Something went wrong.';
    let live = qs('#CartLiveRegion');
    if (!live) {
      live = document.createElement('div');
      live.id = 'CartLiveRegion';
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      document.body.appendChild(live);
    }
    live.textContent = msg;

    // Visible toast inside the cart drawer (e.g. "All 1 items are in your cart")
    const drawer = qs('[data-drawer="cart"]');
    const head = drawer && qs('.cart-drawer__head', drawer);
    if (head) {
      let toast = qs('.cart-toast', drawer);
      if (!toast) {
        toast = document.createElement('div');
        toast.className = 'cart-toast';
        toast.setAttribute('role', 'alert');
        head.after(toast);
      }
      toast.textContent = msg;
      toast.hidden = false;
      clearTimeout(toast._hideTimer);
      toast._hideTimer = setTimeout(() => { toast.hidden = true; }, 5000);
    }
  }

  /* ---------- Product forms ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      if (this._bound) return;
      this._bound = true;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const btn = qs('[type="submit"]', this.form);
        const fd = new FormData(this.form);
        const item = { id: parseInt(fd.get('id'), 10), quantity: parseInt(fd.get('quantity'), 10) || 1 };
        const plan = fd.get('selling_plan');
        if (plan) item.selling_plan = parseInt(plan, 10);
        const props = {};
        for (const [k, v] of fd.entries()) {
          const m = k.match(/^properties\[(.+)\]$/);
          if (m && v) props[m[1]] = v;
        }
        if (Object.keys(props).length) item.properties = props;
        withLoading(btn, Cart.add(item)).catch(announceError);
      });
    }
  }
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* Bundle scent/variant pills (bundle cards) */
  document.addEventListener('click', (e) => {
    const pill = e.target.closest('[data-bundle-variant]');
    if (!pill) return;
    e.preventDefault();
    const card = pill.closest('.bundle-card');
    if (!card) return;
    qsa('[data-bundle-variant]', card).forEach(p => p.setAttribute('aria-pressed', String(p === pill)));
    const idInput = qs('form input[name="id"]', card);
    if (idInput) idInput.value = pill.dataset.bundleVariant;
    // keep the floating quick-add pill in sync with the featured card's scent
    if (card.classList.contains('bundle-card--featured')) {
      const floatBtn = qs('[data-bundle-float] [data-quick-add]');
      if (floatBtn) floatBtn.dataset.quickAdd = pill.dataset.bundleVariant;
    }
  });

  /* Generic variant pills: any [data-variant-pill] inside a [data-variant-scope]
     retargets that scope's quick-add button (e.g. ecosystem cards). */
  document.addEventListener('click', (e) => {
    const pill = e.target.closest('[data-variant-pill]');
    if (!pill) return;
    e.preventDefault();
    const scope = pill.closest('[data-variant-scope]');
    if (!scope) return;
    qsa('[data-variant-pill]', scope).forEach(p => p.setAttribute('aria-pressed', String(p === pill)));
    const btn = qs('[data-quick-add]', scope);
    if (btn) btn.dataset.quickAdd = pill.dataset.variantPill;
  });

  /* Quick add buttons */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quick-add]');
    if (!btn) return;
    e.preventDefault();
    // Rapid re-taps while the add is in flight would queue duplicate adds.
    if (btn.classList.contains('is-loading')) return;
    const item = { id: parseInt(btn.dataset.quickAdd, 10), quantity: parseInt(btn.dataset.qty, 10) || 1 };
    const plan = btn.dataset.sellingPlan;
    if (plan) item.selling_plan = parseInt(plan, 10);
    withLoading(btn, Cart.add(item)).then(() => {
      if (!btn.classList.contains('btn-icon')) return;
      const original = btn.innerHTML;
      btn.classList.add('is-added');
      btn.innerHTML = '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
      setTimeout(() => { btn.classList.remove('is-added'); btn.innerHTML = original; }, 1200);
    }).catch(announceError);
  });

  /* Try-all-scents link: adds one of every AVAILABLE scent variant (the
     Liquid only lists purchasable ids), so Mix & Match tiers apply in cart. */
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-add-pair]');
    if (!btn) return;
    e.preventDefault();
    if (btn.classList.contains('is-loading')) return;
    const ids = btn.dataset.addPair.split(',').map(v => parseInt(v, 10)).filter(Boolean);
    if (ids.length < 1) return;
    const chain = ids.reduce((p, id) => p.then(() => Cart.add({ id, quantity: 1 })), Promise.resolve());
    withLoading(btn, chain).catch(announceError);
  });

  /* Bundle cards: scent pills swap the chosen variant id inside the card's
     Add button list (scent variants share one price, so totals hold). */
  document.addEventListener('click', (e) => {
    const pill = e.target.closest('[data-bundle-scent]');
    if (!pill) return;
    const card = pill.closest('[data-bundle-card]');
    const group = pill.closest('[data-scent-ids]');
    const btn = card && qs('[data-add-pair]', card);
    if (!btn || !group) return;
    const family = group.dataset.scentIds.split(',').filter(Boolean);
    btn.dataset.addPair = btn.dataset.addPair.split(',')
      .map(id => (family.includes(id) ? pill.dataset.bundleScent : id)).join(',');
    qsa('[data-bundle-scent]', group).forEach(b => b.setAttribute('aria-pressed', b === pill ? 'true' : 'false'));
  });

  /* Product switcher: centre the active tab in the strip on load. The
     comparison fold starts closed (short mobile pages) and opens on desktop. */
  function initNavAndFolds() {
    const active = qs('.prodnav__item.is-active');
    if (active) {
      const strip = active.closest('[data-prodnav-strip]');
      if (strip) strip.scrollLeft = active.offsetLeft - (strip.offsetWidth - active.offsetWidth) / 2;
    }
    if (window.matchMedia('(min-width: 750px)').matches) {
      qsa('[data-compare-fold]').forEach(d => { d.open = true; });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initNavAndFolds);
  else initNavAndFolds();

  /* Refill tiers (multi-purpose): each option is a combo of variant ids added
     one unit at a time (duplicates merge to quantity), priced by the ladder.
     Keeps the ATC button and sticky bar in sync with the checked tier. */
  function initRefillTiers() {
    const radios = qsa('[data-refill-tier]');
    const btn = qs('[data-refill-atc]');
    if (!radios.length || !btn) return;
    function apply(r) {
      if (!r) return;
      btn.dataset.addPair = r.dataset.ids;
      btn.disabled = false;
      const lbl = qs('[data-tier-atc-label]', btn);
      if (lbl) lbl.textContent = 'Add ' + r.dataset.label + ' — ' + formatMoney(parseInt(r.dataset.total, 10));
      const sBtn = qs('.sticky-atc__btn');
      if (sBtn) {
        sBtn.dataset.addPair = r.dataset.ids;
        delete sBtn.dataset.quickAdd;
        sBtn.disabled = false;
      }
      const sPrice = qs('.sticky-atc__price');
      if (sPrice) sPrice.textContent = formatMoney(parseInt(r.dataset.total, 10));
    }
    radios.forEach(r => r.addEventListener('change', () => apply(r)));
    apply(radios.find(r => r.checked));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initRefillTiers);
  else initRefillTiers();

  /* ---------- Interaction analytics (Phase 6) ----------
     Named custom events through Shopify's analytics bus (readable by web
     pixels / GA4 via custom pixel). No libraries, no own network calls,
     and it must never break shopping — everything is try/caught. */
  function track(name, payload) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload || {});
      }
    } catch (e) { /* never interrupt the customer */ }
  }
  document.addEventListener('click', (e) => {
    if (!e.target || !e.target.closest) return;
    let el;
    if ((el = e.target.closest('[data-quick-add]'))) track('dsslv_quick_add', { variant: el.dataset.quickAdd });
    else if ((el = e.target.closest('[data-add-pair]'))) track('dsslv_bundle_add', { ids: el.dataset.addPair });
    else if ((el = e.target.closest('.prodnav__item'))) track('dsslv_prodnav_click', { label: el.textContent.trim() });
    else if (e.target.closest('.tier-builder') || e.target.closest('a[href$="#bundles"]')) track('dsslv_builder_open', {});
    else if (e.target.closest('.cart-checkout-btn')) track('dsslv_checkout_click', {});
  }, { passive: true });
  document.addEventListener('change', (e) => {
    if (e.target && e.target.closest && e.target.closest('.ptier')) track('dsslv_tier_select', { qty: e.target.value });
  });

  /* Scent line: one-line explainer per variant (variant metafields), swapped
     whenever the picker lands on a new variant. */
  document.addEventListener('variant:changed', (e) => {
    const v = e.detail && e.detail.variant;
    if (!v) return;
    qsa('[data-scent-for]').forEach(p => { p.hidden = String(v.id) !== p.dataset.scentFor; });
  });

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      if (!this.input) return;
      // Guard: connectedCallback re-runs when the drawer is portaled to <body>
      // (disconnect + reconnect) — without this, listeners double up and one
      // click fires two cart changes (the second with a stale line key).
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-quantity-minus]');
        const plus = e.target.closest('[data-quantity-plus]');
        if (!minus && !plus) return;
        e.preventDefault();
        const step = plus ? 1 : -1;
        const min = parseInt(this.input.min, 10) || 0;
        const next = Math.max(min, (parseInt(this.input.value, 10) || 0) + step);
        if (next !== parseInt(this.input.value, 10)) {
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart drawer interactions (delegated — survives re-render) ---------- */
  const cartDrawer = qs('[data-drawer="cart"]');
  if (cartDrawer) {
    cartDrawer.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-cart-remove]');
      if (removeBtn) {
        e.preventDefault();
        const line = removeBtn.closest('[data-cart-line]');
        if (line) line.classList.add('is-updating');
        Cart.change({ id: removeBtn.dataset.cartRemove, quantity: 0 }).catch(announceError);
      }
      const upgradeBtn = e.target.closest('[data-upgrade-swap]');
      if (upgradeBtn) {
        e.preventDefault();
        const fromKey = upgradeBtn.dataset.fromKey;
        const toId = parseInt(upgradeBtn.dataset.upgradeSwap, 10);
        if (!toId) return;
        withLoading(upgradeBtn, (async () => {
          if (fromKey) await Cart.change({ id: fromKey, quantity: 0 });
          await Cart.add({ id: toId, quantity: 1 }, false);
        })()).catch(announceError);
      }
      const noteToggle = e.target.closest('[data-note-toggle]');
      if (noteToggle) {
        e.preventDefault();
        const wrap = qs('[data-note-wrap]', cartDrawer);
        if (wrap) wrap.hidden = !wrap.hidden;
      }
    });
    cartDrawer.addEventListener('change', (e) => {
      const input = e.target.closest('.quantity__input');
      if (input && input.dataset.line) {
        const line = input.closest('[data-cart-line]');
        if (line) line.classList.add('is-updating');
        Cart.change({ id: input.dataset.line, quantity: parseInt(input.value, 10) || 0 }).catch((err) => {
          announceError(err); Cart.refresh();
        });
      }
      const note = e.target.closest('[data-cart-note]');
      if (note) Cart.update({ note: note.value }).catch(() => {});
    });
  }

  /* ---------- Product review topic filters ---------- */
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-review-filter]');
    if (!chip) return;
    e.preventDefault();
    const section = chip.closest('.product-reviews');
    if (!section) return;
    const topic = chip.dataset.reviewFilter;
    qsa('[data-review-filter]', section).forEach(c => c.setAttribute('aria-pressed', String(c === chip)));
    qsa('[data-review-card]', section).forEach(card => {
      const topics = (card.dataset.topics || '').split(/\s+/);
      card.hidden = topic !== 'all' && !topics.includes(topic);
    });
  });

  /* ---------- Header glass on scroll ---------- */
  const header = qs('[data-header]');
  if (header) {
    const setGlass = () => header.classList.toggle('header--glass', window.scrollY > 12);
    setGlass();
    window.addEventListener('scroll', setGlass, { passive: true });
  }

  /* ---------- Announcement rotation ---------- */
  const announcement = qs('[data-announcement]');
  if (announcement) {
    const items = qsa('.announcement__item', announcement);
    if (items.length > 1) {
      let i = 0;
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, 4200);
    }
  }

  /* ---------- Accordions ---------- */
  document.addEventListener('click', (e) => {
    const q = e.target.closest('[data-accordion-toggle]');
    if (!q) return;
    const item = q.closest('[data-accordion-item]');
    if (!item) return;
    const isOpen = item.hasAttribute('data-open');
    const group = item.closest('[data-accordion-single]');
    if (group && !isOpen) {
      qsa('[data-accordion-item][data-open]', group).forEach(other => {
        other.removeAttribute('data-open');
        const ob = qs('[data-accordion-toggle]', other);
        if (ob) ob.setAttribute('aria-expanded', 'false');
      });
    }
    item.toggleAttribute('data-open', !isOpen);
    q.setAttribute('aria-expanded', String(!isOpen));
  });

  /* ---------- Purchase toggle (one-time vs subscribe) ---------- */
  qsa('[data-purchase-toggle]').forEach(toggle => {
    const scope = toggle.closest('section') || document;
    toggle.addEventListener('click', (e) => {
      const btn = e.target.closest('.purchase-toggle__btn');
      if (!btn) return;
      qsa('.purchase-toggle__btn', toggle).forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
      const mode = btn.dataset.mode; // 'one-time' | 'subscribe'
      qsa('[data-plan-input]', scope).forEach(input => {
        input.disabled = mode !== 'subscribe' || !input.dataset.planId;
        input.value = mode === 'subscribe' ? (input.dataset.planId || '') : '';
      });
      qsa('[data-price-onetime]', scope).forEach(el => el.hidden = mode === 'subscribe');
      qsa('[data-price-subscribe]', scope).forEach(el => el.hidden = mode !== 'subscribe');
    });
  });

  /* ---------- Variant picker (PDP) ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = qs('[data-product-json]', this) || qs('#ProductJson', this.closest('.product') || document);
      if (!dataEl) return;
      try { this.product = JSON.parse(dataEl.textContent); } catch (e) { return; }
      this.selected = {};
      const current = this.product.variants.find(v => String(v.id) === this.dataset.selected) || this.product.variants[0];
      (current.options || []).forEach((val, i) => this.selected[i] = val);
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (e) => {
        const pill = e.target.closest('.option-pill');
        if (!pill) return;
        e.preventDefault();
        this.selected[parseInt(pill.dataset.optionIndex, 10)] = pill.dataset.value;
        qsa(`.option-pill[data-option-index="${pill.dataset.optionIndex}"]`, this)
          .forEach(p => p.setAttribute('aria-pressed', String(p === pill)));
        this.apply();
      });
    }
    apply() {
      const values = Object.keys(this.selected).sort().map(k => this.selected[k]);
      const variant = this.product.variants.find(v => v.options.every((o, i) => o === values[i]));
      const root = this.closest('.product') || document;
      const idInput = qs('input[name="id"]', root);
      const btn = qs('product-form [type="submit"]', root);
      if (!variant) {
        if (btn) { btn.disabled = true; qs('.btn__label', btn).textContent = D.strings.unavailable || 'Unavailable'; }
        return;
      }
      if (idInput) idInput.value = variant.id;
      if (btn) {
        btn.disabled = !variant.available;
        const label = qs('.btn__label', btn);
        if (label) label.textContent = variant.available ? (D.strings.addToCart || 'Add to cart') : (D.strings.soldOut || 'Sold out');
      }
      qsa('[data-price]', root).forEach(el => el.textContent = formatMoney(variant.price));
      qsa('[data-compare-price]', root).forEach(el => {
        if (variant.compare_at_price > variant.price) { el.hidden = false; el.textContent = formatMoney(variant.compare_at_price); }
        else el.hidden = true;
      });
      // {{ product | json }} carries the variant image on featured_image
      // (featured_media only exists in Liquid), and the swap must go through
      // swapMainImage so srcset gets cleared — a bare src change loses to it.
      const media = (variant.featured_media && variant.featured_media.preview_image) || variant.featured_image;
      if (media && media.src) {
        const join = media.src.indexOf('?') > -1 ? '&' : '?';
        const gallery = qs('.product__gallery', root) || root;
        swapMainImage(gallery, media.src + join + 'width=1100');
        const base = media.src.split('?')[0];
        qsa('[data-thumb]', gallery).forEach(t => t.setAttribute('aria-current', String((t.dataset.thumb || '').split('?')[0] === base)));
        const stickyImg = qs('[data-sticky-atc] .sticky-atc__img');
        if (stickyImg) stickyImg.src = media.src + join + 'width=96';
      }
      // keep the sticky add-to-cart bar targeting the selected variant
      const stickyBtn = qs('[data-sticky-atc] [data-quick-add]');
      if (stickyBtn) stickyBtn.dataset.quickAdd = variant.id;
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
      this.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { variant } }));
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Product gallery: main image swaps (thumbs + variants) ---------- */
  // Calm crossfade: fade out, preload the new frame, swap only once it's
  // ready AND the fade-out has had time to breathe, then fade back in.
  // srcset MUST be cleared on swap: with it present the browser keeps
  // choosing from the old candidates and a bare src change never shows.
  function swapMainImage(scope, url) {
    const main = qs('[data-main-image]', scope);
    if (!main || main.src === url) return;
    const started = performance.now();
    main.style.opacity = 0;
    const pre = new Image();
    const swap = () => {
      const wait = Math.max(0, 380 - (performance.now() - started));
      setTimeout(() => {
        main.src = url;
        main.srcset = '';
        requestAnimationFrame(() => requestAnimationFrame(() => { main.style.opacity = 1; }));
      }, wait);
    };
    pre.onload = swap;
    pre.onerror = swap;
    pre.src = url;
  }
  document.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-thumb]');
    if (!thumb) return;
    e.preventDefault();
    const root = thumb.closest('.product__gallery');
    swapMainImage(root, thumb.dataset.thumb);
    qsa('[data-thumb]', root).forEach(t => t.setAttribute('aria-current', String(t === thumb)));
  });

  /* ---------- Generic carousel arrows ---------- */
  // <button data-scroll-btn="[selector-of-scroller]" data-dir="prev|next">
  // steps by one child's width; used by the PDP thumb strip and the mobile
  // review carousel.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-scroll-btn]');
    if (!btn) return;
    const scroller = qs(btn.dataset.scrollBtn);
    if (!scroller) return;
    const child = scroller.firstElementChild;
    const gap = parseFloat(getComputedStyle(scroller).columnGap || getComputedStyle(scroller).gap) || 12;
    const step = (child ? child.getBoundingClientRect().width : scroller.clientWidth * 0.8) + gap;
    scroller.scrollBy({ left: btn.dataset.dir === 'prev' ? -step : step, behavior: 'smooth' });
  });

  /* ---------- Sticky ATC (PDP) + floating bundle pill (home) ---------- */
  function bindSticky(sentinelSel, barSel, mode, htmlClass) {
    const sentinel = qs(sentinelSel);
    const bar = qs(barSel);
    if (!sentinel || !bar || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(([entry]) => {
      const passed = entry.boundingClientRect.bottom < 0;
      const on = mode === 'after' ? passed : !entry.isIntersecting;
      bar.classList.toggle('is-visible', on);
      // Lets third-party overlays (Shopify Inbox bubble) react to the bar
      if (htmlClass) {
        document.documentElement.classList.toggle(htmlClass, on);
        if (htmlClass === 'sticky-atc-visible' && D.InboxBubble) D.InboxBubble.lift(on);
      }
    }, { threshold: 0 }).observe(sentinel);
  }
  bindSticky('[data-buy-anchor]', '[data-sticky-atc]', 'notvisible', 'sticky-atc-visible');
  bindSticky('[data-bundles-anchor]', '[data-bundle-float]', 'after');

  /* ---------- Smooth anchor scrolling with header offset ---------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"], a[href^="/#"]');
    if (!link) return;
    const hash = link.getAttribute('href').replace(/^\//, '');
    if (hash.length < 2) return;
    const target = qs(hash);
    if (!target) return; // cross-page anchors navigate normally
    // #bundles also exists inside the popup once it has loaded; those clicks
    // belong to the launcher handler below, not to page scrolling.
    if (target.closest('[data-bb-modal]')) return;
    e.preventDefault();
    const top = target.getBoundingClientRect().top + window.scrollY - (qs('[data-header]')?.offsetHeight || 0) - 8;
    window.scrollTo({ top, behavior: 'smooth' });
    Drawers.close();
  });

  /* ---------- Pack tier picker (PDP) ---------- */
  // The 4-option selector is the only quantity control: the checked tier
  // drives the form's hidden quantity input, the add-to-cart label
  // ("Add 3 packs — $76.38") and the sticky bar (price, packs and add qty).
  function updateTierAtc(node) {
    const root = (node && node.closest && node.closest('.product')) || qs('.product') || document;
    const checked = qs('[data-pack-tier]:checked', root);
    if (!checked) return;
    const qtyInput = qs('input[name="quantity"]', root);
    if (qtyInput) qtyInput.value = checked.value;
    const total = parseInt(checked.dataset.total, 10);
    const btn = qs('product-form [type="submit"]', root);
    if (btn && !btn.disabled) {
      const label = qs('[data-tier-atc-label]', btn) || qs('.btn__label', btn);
      if (label) label.textContent = 'Add ' + checked.dataset.label + ' — ' + formatMoney(total);
    }
    const sticky = qs('[data-sticky-atc]');
    if (sticky) {
      const sBtn = qs('[data-quick-add]', sticky);
      if (sBtn) sBtn.dataset.qty = checked.value;
      const sPrice = qs('.sticky-atc__price', sticky);
      if (sPrice) sPrice.textContent = formatMoney(total);
      const sUses = qs('.sticky-atc__uses', sticky);
      if (sUses) sUses.textContent = ' · ' + checked.dataset.label;
    }
  }
  document.addEventListener('change', (e) => {
    const tier = e.target.closest('[data-pack-tier]');
    if (tier) updateTierAtc(tier);
  });
  // VariantPicker resets the button label on scent switches; re-assert.
  document.addEventListener('variant:changed', (e) => updateTierAtc(e.target));
  qsa('[data-pack-tiers]').forEach(updateTierAtc);

  /* ---------- Mobile bundle carousel: centre the featured card on load ---------- */
  function centreFeaturedBundle() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const grid = qs('.bundles__grid');
    const feat = grid && qs('.bundle-card--featured', grid);
    if (!feat) return;
    const left = feat.getBoundingClientRect().left - grid.getBoundingClientRect().left + grid.scrollLeft;
    grid.scrollTo({ left: left - (grid.clientWidth - feat.offsetWidth) / 2, behavior: 'instant' });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', centreFeaturedBundle);
  else centreFeaturedBundle();

  /* ---------- Build Your Home (bundle builder) ---------- */
  // Tiers arrive from the section's schema (data-tiers = [[qty,pct],...]) and
  // MUST mirror the store's Mix & Match automatic discounts (any 2 packs 15%,
  // any 3 20%, any 4-5 25%, any 6+ 30%, TOTAL UNITS across products, duplicates
  // included). Saving is floored PER UNIT to match Shopify's allocation.
  function initBuildHome(scope) {
    // Called with no args on page load (or a DOMContentLoaded Event), and
    // with the injected section root when the popup builder loads.
    const root = (scope && scope.nodeType === 1) ? scope : qs('[data-build-home]');
    if (!root || root._bbBound) return;
    root._bbBound = true;

    const TIERS = (JSON.parse(root.dataset.tiers || '[]')).map(t => ({ qty: t[0], pct: t[1] })).sort((a, b) => a.qty - b.qty);
    const MAXQ = TIERS.length ? TIERS[TIERS.length - 1].qty : 6;
    const SHIP_CENTS = parseInt(root.dataset.shipThreshold, 10) || 0;
    const SHIP_QTY = parseInt(root.dataset.shipQty, 10) || 0;

    const items = qsa('[data-bb-item]', root).map(el => ({
      el,
      idx: parseInt(el.dataset.idx, 10),
      room: el.dataset.room,
      title: el.dataset.title,
      price: parseInt(el.dataset.price, 10),
      uses: parseInt(el.dataset.uses, 10) || 0,
      unit: el.dataset.unit || '',
      fixed: el.dataset.fixed === 'true',
      prefix: el.dataset.prefix || '',
      img: el.dataset.img
    }));
    const state = { qty: {}, variant: {} };
    items.forEach(it => { state.qty[it.idx] = 0; state.variant[it.idx] = it.el.dataset.variant; });

    const el = {
      count: qs('[data-bb-count]', root),
      minifill: qs('[data-bb-minifill]', root),
      trackfill: qs('[data-bb-trackfill]', root),
      marks: qsa('[data-bb-mark]', root),
      goal: qs('[data-bb-goal]', root),
      reco: qs('[data-bb-reco]', root),
      recoImg: qs('[data-bb-reco-img]', root),
      recoName: qs('[data-bb-reco-name]', root),
      recoMsg: qs('[data-bb-reco-msg]', root),
      bar: qs('[data-bb-bar]', root),
      barUnits: qs('[data-bb-bar-units]', root),
      barCount: qs('[data-bb-bar-count]', root),
      barPct: qs('[data-bb-bar-pct]', root),
      barNext: qs('[data-bb-bar-next]', root),
      barShip: qs('[data-bb-bar-shiptext]', root),
      barSub: qs('[data-bb-bar-subtotal]', root),
      barSave: qs('[data-bb-bar-save]', root),
      checkout: qs('[data-bb-checkout]', root)
    };

    function plural(n) { return n === 1 ? '' : 's'; }

    function totals() {
      let units = 0, subtotal = 0;
      items.forEach(it => { const q = state.qty[it.idx]; units += q; subtotal += q * it.price; });
      let pct = 0;
      TIERS.forEach(t => { if (units >= t.qty) pct = t.pct; });
      let saving = 0;
      items.forEach(it => { saving += state.qty[it.idx] * Math.floor(it.price * pct / 100); });
      const discounted = subtotal - saving;
      const nextTier = TIERS.find(t => units < t.qty) || null;
      const shipFree = (SHIP_QTY > 0 && units >= SHIP_QTY) || (units > 0 && discounted >= SHIP_CENTS);
      return { units, subtotal, pct, saving, discounted, nextTier, shipFree };
    }

    // Recommend, in order: unlock the next tier -> complete a room -> reach
    // free shipping. An unselected product serves the first two at once.
    function recommend(t) {
      const unselected = items.filter(it => state.qty[it.idx] === 0);
      const cheapest = items.slice().sort((a, b) => a.price - b.price)[0];
      if (t.nextTier) {
        if (unselected.length) return { it: unselected[0], msg: 'Unlocks ' + t.nextTier.pct + '% OFF' };
        return { it: cheapest, msg: 'One more unlocks ' + t.nextTier.pct + '% OFF' };
      }
      if (unselected.length) return { it: unselected[0], msg: 'Completes your ' + unselected[0].room.toLowerCase() };
      if (!t.shipFree) return { it: cheapest, msg: 'Gets you to FREE shipping' };
      return null;
    }

    function render() {
      const t = totals();

      items.forEach(it => {
        const q = state.qty[it.idx];
        it.el.classList.toggle('is-in', q > 0);
        qs('[data-bb-qty]', it.el).textContent = q;
        // Total value line scales with quantity. Fixed-yield products state
        // exact totals ("18 refills"); variable-dosage keep "Up to".
        const usesEl = qs('[data-bb-uses]', it.el);
        if (usesEl) {
          if (it.uses > 0) {
            const total = it.uses * Math.max(q, 1);
            const unitPlural = it.unit === 'wash' ? 'washes' : it.unit + 's';
            usesEl.hidden = false;
            usesEl.textContent = (it.fixed ? '' : 'Up to ') + total + ' ' + (it.prefix ? it.prefix + ' ' : '') + unitPlural;
          } else {
            usesEl.hidden = true;
          }
        }
        const roomEl = qs('[data-bb-room="' + it.idx + '"]', root);
        if (roomEl) {
          roomEl.classList.toggle('is-done', q > 0);
          roomEl.setAttribute('aria-label', it.room + (q > 0 ? ': added' : ': not added yet'));
        }
      });

      el.count.textContent = (t.units <= MAXQ ? t.units + ' of ' + MAXQ : t.units) + ' products';
      const fillPct = Math.min(t.units / MAXQ * 100, 100) + '%';
      el.minifill.style.width = fillPct;
      el.trackfill.style.width = fillPct;
      let current = null;
      TIERS.forEach(tt => { if (t.units >= tt.qty) current = tt.qty; });
      el.marks.forEach(m => {
        const q = parseInt(m.dataset.bbMark, 10);
        m.classList.toggle('is-done', t.units >= q);
        m.classList.toggle('is-current', q === current);
      });
      el.goal.textContent = t.nextTier
        ? (t.units === 0
            ? 'Add any ' + t.nextTier.qty + ' products to unlock ' + t.nextTier.pct + '% off'
            : 'Add ' + (t.nextTier.qty - t.units) + ' more product' + plural(t.nextTier.qty - t.units) + ' to unlock ' + t.nextTier.pct + '% off')
        : 'Top tier unlocked: ' + t.pct + '% off everything';

      const rec = recommend(t);
      if (el.reco) {
        el.reco.hidden = !rec;
        // The reco card and the plain next-goal text share the progress
        // panel's third column; show whichever applies.
        const goalWrap = qs('[data-bb-goal-wrap]', root);
        if (goalWrap) goalWrap.hidden = !!rec;
        if (rec) {
          el.reco.dataset.target = rec.it.idx;
          el.recoImg.src = rec.it.img;
          el.recoImg.alt = rec.it.title;
          el.recoName.textContent = rec.it.title;
          el.recoMsg.textContent = rec.msg;
        }
      }

      if (el.bar) {
        el.barUnits.textContent = t.units;
        el.barCount.textContent = t.units + ' pack' + (t.units === 1 ? '' : 's');
        el.barPct.textContent = t.pct > 0 ? t.pct + '% OFF' : 'No discount yet';
        el.barNext.textContent = t.nextTier
          ? 'Add ' + (t.units === 0 ? 'any ' + t.nextTier.qty : (t.nextTier.qty - t.units) + ' more') + ' for ' + t.nextTier.pct + '% OFF'
          : 'Top tier unlocked';
        el.barShip.innerHTML = t.shipFree
          ? '<strong>FREE SHIPPING unlocked</strong>'
          : '<strong>' + formatMoney(Math.max(SHIP_CENTS - t.discounted, 0)) + '</strong> from FREE shipping';
        el.barSub.textContent = formatMoney(t.discounted);
        el.barSave.hidden = t.saving === 0;
        el.barSave.textContent = 'You save ' + formatMoney(t.saving);
        el.checkout.disabled = t.units === 0;
      }
    }

    root.addEventListener('click', (e) => {
      const step = e.target.closest('[data-bb-add], [data-bb-plus], [data-bb-minus]');
      if (step) {
        const card = e.target.closest('[data-bb-item]');
        const i = parseInt(card.dataset.idx, 10);
        if (step.hasAttribute('data-bb-add')) state.qty[i] = 1;
        else state.qty[i] = Math.max(0, Math.min(12, state.qty[i] + (step.hasAttribute('data-bb-plus') ? 1 : -1)));
        render(); return;
      }
      const variant = e.target.closest('[data-bb-variant]');
      if (variant) {
        const card = e.target.closest('[data-bb-item]');
        state.variant[card.dataset.idx] = variant.dataset.bbVariant;
        qsa('[data-bb-variant]', card).forEach(b => b.setAttribute('aria-pressed', String(b === variant)));
        return;
      }
      const reco = e.target.closest('[data-bb-reco]');
      if (reco && reco.dataset.target !== undefined) {
        const i = parseInt(reco.dataset.target, 10);
        state.qty[i] = state.qty[i] + 1;
        render();
        const card = items.find(it => it.idx === i);
        if (card) card.el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        return;
      }
    });

    // Checkout: add the built pack to the cart, then straight to checkout.
    if (el.checkout) {
      el.checkout.addEventListener('click', () => {
        const lines = [];
        items.forEach(it => {
          const q = state.qty[it.idx];
          if (q > 0) lines.push({ id: parseInt(state.variant[it.idx], 10), quantity: q });
        });
        if (!lines.length) return;
        const req = fetch(D.routes.cartAdd + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: lines })
        }).then(r => {
          if (!r.ok) throw new Error('Cart error');
          window.location.href = '/checkout';
          return new Promise(() => {}); // hold the spinner through navigation
        });
        withLoading(el.checkout, req).catch(announceError);
      });
    }

    // Inside the popup the summary is the modal's docked footer: always on,
    // and no chat-bubble lift (the overlay covers the page anyway).
    const inModal = !!root.closest('[data-bb-modal]');
    if (el.bar && inModal) {
      el.bar.classList.add('is-visible');
      el.bar.setAttribute('aria-hidden', 'false');
    }
    // Sticky summary docks while the section is on screen (all widths); the
    // Inbox bubble lifts clear of it (taller offset than the PDP sticky ATC).
    else if (el.bar && 'IntersectionObserver' in window) {
      let barOn = false;
      const setBar = (on) => {
        if (on === barOn) return;
        barOn = on;
        el.bar.classList.toggle('is-visible', on);
        el.bar.setAttribute('aria-hidden', String(!on));
        document.documentElement.classList.toggle('bb-bar-visible', on);
        // Lift the chat bubble by the bar's MEASURED height (it varies with
        // content and already includes the safe-area padding), plus a gap.
        if (D.InboxBubble) {
          const h = Math.round(el.bar.getBoundingClientRect().height);
          D.InboxBubble.lift(on, (h + 16) + 'px');
        }
      };
      new IntersectionObserver(([entry]) => {
        setBar(entry.isIntersecting);
      }, { threshold: 0.05 }).observe(root);
    }

    render();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initBuildHome);
  else initBuildHome();

  /* ---------- Build Your Pack launcher + popup ---------- */
  // The floating pill opens the homepage Build Your Home section in a popup
  // on any page — fetched once through the Section Rendering API so the
  // homepage section stays the single source of configuration. On pages that
  // already render the builder (the homepage), it scrolls to it instead.
  // Nav links to /#bundles get the same upgrade; a failed fetch falls back
  // to plain navigation.
  const BuilderModal = {
    loaded: false,
    isOpen: false,
    lastFocus: null,
    load() {
      if (this.loaded) return Promise.resolve();
      // The Section Rendering API wants the JSON template's full section id
      // (template--<digits>__bundles) and those digits change with every
      // theme version — so fetch the homepage once and lift the rendered
      // section out of it instead. DOMParser never executes scripts.
      return fetch('/')
        .then(r => { if (!r.ok) throw new Error('home http ' + r.status); return r.text(); })
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const parsed = doc.querySelector('[data-build-home]');
          const wrap = parsed && (parsed.closest('.shopify-section') || parsed);
          if (!wrap) throw new Error('builder section not found');
          const body = qs('[data-bb-modal-body]');
          body.innerHTML = '';
          body.appendChild(document.adoptNode(wrap));
          this.loaded = true;
          const root = qs('[data-build-home]', body);
          if (root) initBuildHome(root);
        });
    },
    show(on) {
      const modal = qs('[data-bb-modal]');
      if (!modal) return;
      this.isOpen = on;
      // The html class also hides the Inbox bubble, which z-indexes itself
      // above the overlay and can't be layered from here.
      document.documentElement.classList.toggle('bb-modal-open', on);
      if (on) {
        this.lastFocus = document.activeElement;
        modal.hidden = false;
        requestAnimationFrame(() => modal.classList.add('is-open'));
        document.documentElement.style.overflow = 'hidden';
        const close = qs('.bb-modal__close', modal);
        if (close) close.focus({ preventScroll: true });
      } else {
        modal.classList.remove('is-open');
        document.documentElement.style.overflow = '';
        setTimeout(() => { modal.hidden = true; }, 320);
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
      }
    },
    open(fallbackHref) {
      const onPage = qs('.main-content [data-build-home]');
      if (onPage) {
        const top = onPage.getBoundingClientRect().top + window.scrollY - (qs('[data-header]')?.offsetHeight || 0) - 8;
        window.scrollTo({ top, behavior: 'smooth' });
        return;
      }
      this.show(true);
      this.load().catch(() => {
        this.show(false);
        if (fallbackHref) window.location.href = fallbackHref;
      });
    }
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-bb-launch]')) {
      e.preventDefault();
      BuilderModal.open('/#bundles');
      return;
    }
    if (e.target.closest('[data-bb-modal-close]')) {
      e.preventDefault();
      BuilderModal.show(false);
      return;
    }
    const link = e.target.closest('a[href$="#bundles"]');
    if (link && !qs('.main-content [data-build-home]')) {
      e.preventDefault();
      Drawers.close();
      BuilderModal.open(link.getAttribute('href'));
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && BuilderModal.isOpen) BuilderModal.show(false);
  });

  /* ---------- Shopify Inbox bubble positioning ---------- */
  // The Inbox bubble (<shopify-chat>, open shadow root) anchors its button via
  // bottom: calc(var(--shopify-agent-activator-offset) + ...) in its adopted
  // stylesheets (widget update, Aug 2026 — the old margin-bottom hack is now
  // pinned to 0 by the widget's own rules and MUST NOT be used: combined with
  // the new 20px anchor it pushed the bubble off-screen). The widget writes
  // that custom property to the host's inline style itself (20px), so setting
  // the same property with !important priority is the reliable lever: rest
  // pins the widget default (low corner), lifted clears the sticky ATC bar and
  // the pack-builder bar (both ~75px tall).
  const InboxBubble = {
    lifted: false,
    REST: '20px',
    // Must clear both bottom bars incl. iPhone safe area: sticky ATC ~82px,
    // pack-builder bar ~97px, + env() home-indicator inset. The widget's
    // bottom calc() nests our calc() var without complaint (verified).
    LIFTED: 'calc(110px + env(safe-area-inset-bottom, 0px))',
    apply() {
      const chat = document.querySelector('shopify-chat');
      if (!chat) return false;
      chat.style.setProperty('--shopify-agent-activator-offset', this.lifted ? (this.customOffset || this.LIFTED) : this.REST, 'important');
      // The var feeds a calc()'d bottom on the inner nudge, but the widget's
      // formula keeps drifting between releases (Aug 2026: 110px in computed
      // to 88px out, and iOS ignored it entirely). Belt and braces: pin the
      // nudge's bottom inline with !important while lifted — deterministic
      // whatever their calc does — and hand back control at rest.
      const n = chat.shadowRoot && chat.shadowRoot.querySelector('.activator-nudge');
      if (n) {
        n.style.setProperty('transition', 'bottom .35s cubic-bezier(.16, 1, .3, 1)', 'important');
        if (this.lifted) n.style.setProperty('bottom', this.customOffset || this.LIFTED, 'important');
        else n.style.removeProperty('bottom');
      }
      return true;
    },
    lift(on, offset) {
      // Callers with taller bars (the Build Your Home summary) pass their own
      // measured offset; the default LIFTED clears the PDP sticky ATC.
      this.lifted = !!on;
      this.customOffset = on ? (offset || null) : null;
      this.apply();
    },
    watch() {
      const chat = document.querySelector('shopify-chat');
      if (!chat || !chat.shadowRoot || chat._dsslvWatched) return this.apply();
      chat._dsslvWatched = true;
      // Re-apply whenever the widget re-renders its shadow children (it also
      // rewrites the host style attribute, clobbering our override)
      new MutationObserver(() => this.apply()).observe(chat.shadowRoot, { childList: true, subtree: true });
      this.apply();
    }
  };
  D.InboxBubble = InboxBubble;
  // The widget REPLACES its whole <shopify-chat> element during hydration,
  // orphaning any observer attached to the first one. A light maintenance
  // interval re-adopts whichever element is current (one querySelector per
  // second; each fresh element is watched exactly once via _dsslvWatched).
  setInterval(() => {
    const c = document.querySelector('shopify-chat');
    if (c && c.shadowRoot && !c._dsslvWatched) InboxBubble.watch();
  }, 1000);
  InboxBubble.watch();

  /* ---------- iOS bottom-fixed re-anchor ---------- */
  // iOS Safari can freeze bottom-fixed elements mid-screen when their dock
  // transition overlaps a visual-viewport resize — the address bar collapses
  // on the same scroll that shows the sticky ATC, so both bar and chat end
  // up floating where the old viewport bottom was. Once the viewport
  // settles, dirty each element's position style (no visual change) so
  // Safari recomputes its anchor, and re-assert the chat offset.
  if (window.visualViewport) {
    let vvT;
    window.visualViewport.addEventListener('resize', () => {
      clearTimeout(vvT);
      vvT = setTimeout(() => {
        qsa('[data-sticky-atc], [data-bb-bar], .bb-launch').forEach(el => {
          const t = el.style.transition;
          el.style.transition = 'none';
          el.style.bottom = getComputedStyle(el).bottom;
          void el.offsetHeight;
          el.style.bottom = '';
          el.style.transition = t;
        });
        InboxBubble.apply();
      }, 150);
    });
  }

  /* ---------- Boot ---------- */
  // Portal drawers to <body>: Shopify wraps sections in their own stacking
  // contexts (the sticky header wrapper has z-index 90), which traps the
  // mobile nav BELOW the page overlay. Moving drawers to <body> restores
  // correct layering; all events are document-delegated so nothing re-binds.
  qsa('[data-drawer]').forEach(el => {
    if (el.parentElement !== document.body) document.body.appendChild(el);
  });
  document.documentElement.setAttribute('data-motion', D.motion.enabled ? D.motion.intensity : 'off');
  Cart.refresh();
})();
